/**
 * Automation Service
 *
 * Client-side service for interacting with automation-related data:
 * tracked forms, backup jobs, plan tiers, and automation events.
 */

import { supabase } from '@/integrations/supabase/client';
import type { AppDatabase } from '@/integrations/supabase/app-types';
import type { FormProvider } from '@/types/canonical';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PlanTier {
  id: string;
  display_name: string;
  form_limit: number;
  retention_days: number;
  manual_backup_cap: number;
  pricing_type: 'one_time' | 'subscription';
  sort_order: number;
}

export interface Organization {
  id: string;
  name: string;
  plan_tier_id: string;
  timezone: string;
  default_schedule_cron: string;
  trial_ends_at: string | null;
  next_scheduled_backup_at: string | null;
  backup_schedule_jitter_minutes: number;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  jotform_detected_plan: string | null;
  jotform_detected_tier_id: string | null;
  jotform_plan_checked_at: string | null;
  jotform_plan_mismatch_started_at: string | null;
  jotform_plan_grace_ends_at: string | null;
  billing_status: string | null;
  billing_active_price_id: string | null;
  billing_active_price_id_key: string | null;
  billing_cancel_at_period_end: boolean;
  billing_current_period_start: string | null;
  billing_current_period_end: string | null;
  plan_tiers?: PlanTier;
}

export interface TrackedForm {
  id: string;
  organization_id: string;
  provider: FormProvider;
  form_id_external: string;
  name: string | null;
  automation_enabled: boolean;
  schedule_cron: string | null;
  last_backup_at: string | null;
  last_backup_state: 'success' | 'fail' | null;
  next_run_at: string | null;
  manual_backups_used: number;
  provider_status: 'active' | 'disabled' | 'archived' | 'deleted' | 'purged' | null;
  created_at: string;
}

export interface AutomationEvent {
  id: string;
  organization_id: string;
  tracked_form_id: string | null;
  backup_job_id: string | null;
  backup_id: string | null;
  level: 'info' | 'warn' | 'error';
  event_type: string;
  message: string;
  meta: Record<string, unknown> | null;
  created_at: string;
}

export interface PlanUsage {
  formsTracked: number;
  formLimit: number;
  failures24h: number;
  manualRemaining: number;
  manualCap: number;
  retentionDays: number;
  trialEndsAt: string | null;
  tierName: string;
}

// ---------------------------------------------------------------------------
// Organization
// ---------------------------------------------------------------------------

export async function getUserOrganization(): Promise<Organization | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select('*, plan_tiers!organizations_plan_tier_id_fkey(*)')
    .limit(1)
    .single();

  if (error) {
    console.error('[automation] Error fetching org:', error.message);
    return null;
  }

  return data as unknown as Organization;
}

// ---------------------------------------------------------------------------
// Tracked Forms
// ---------------------------------------------------------------------------

export async function getTrackedForms(): Promise<TrackedForm[]> {
  const { data, error } = await supabase
    .from('tracked_forms')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    console.error('[automation] Error fetching tracked forms:', error.message);
    return [];
  }

  return (data || []) as unknown as TrackedForm[];
}

/**
 * Map a provider's form status string to our normalized provider_status enum.
 *
 * Backups run for every state — this value is only used for user-facing
 * display logic (e.g. the daily summary email's "X of Y forms checked" count
 * filters to active forms only).
 */
export function mapProviderStatus(rawStatus: string | undefined | null): TrackedForm['provider_status'] {
  if (!rawStatus) return 'active';
  switch (String(rawStatus).toUpperCase()) {
    case 'ENABLED':
      return 'active';
    case 'DISABLED':
      return 'disabled';
    case 'TRASH':
    case 'TRASHED':
    case 'ARCHIVED':
      return 'archived';
    case 'DELETED':
      return 'deleted';
    case 'PURGED':
      return 'purged';
    default:
      // Unknown status — assume active so we don't accidentally hide a
      // working form from the user's email count.
      return 'active';
  }
}

/**
 * Sync tracked forms from a provider API response.
 *
 * - Upserts every form returned by the provider with its REAL provider_status.
 *   No plan-limit capping. Every form gets backed up regardless of status —
 *   provider_status is used by the email function for display filtering only.
 * - For new orgs that don't yet have a backup schedule, seeds
 *   organizations.next_scheduled_backup_at with a small near-immediate value
 *   so the planner picks the org up on the next 5-minute tick.
 *
 * Spec: docs/BACKUP_SCHEDULING_REDESIGN_2026-04-10.md (Section 6.2 / 9)
 */
export async function syncTrackedForms(
  organizationId: string,
  provider: FormProvider,
  forms: Array<{ id: string; name: string; modified?: string; status?: string }>
): Promise<void> {
  if (forms.length === 0) return;

  const rows = forms.map((f) => ({
    organization_id: organizationId,
    provider,
    form_id_external: String(f.id),
    name: f.name,
    automation_enabled: true,
    last_modified_at: f.modified || null,
    provider_status: mapProviderStatus(f.status),
  }));
  const trackedFormRows = rows as AppDatabase['public']['Tables']['tracked_forms']['Insert'][];

  // Regular upsert (NOT ignoreDuplicates) so we keep updating name and
  // metadata for forms we've seen before. Spec Section 9.
  const { error: upsertError } = await supabase
    .from('tracked_forms')
    .upsert(trackedFormRows as never, {
      onConflict: 'organization_id,provider,form_id_external',
      ignoreDuplicates: false,
    });

  if (upsertError) {
    console.error('[automation] Error syncing tracked forms:', upsertError.message);
    return;
  }

  // If this org has no scheduled backup time yet, seed it now-ish so the
  // planner picks the org up within the next 5 minutes. We only seed when
  // null so that an existing daily schedule is never moved earlier on a
  // re-sync.
  const { data: orgRow } = await supabase
    .from('organizations')
    .select('next_scheduled_backup_at')
    .eq('id', organizationId)
    .single();

  const orgNextRun = (orgRow as { next_scheduled_backup_at: string | null } | null)?.next_scheduled_backup_at;
  if (!orgNextRun) {
    // Small 0–3 minute jitter so a bulk-onboarding wave doesn't all hit the
    // same second. Per Section 6.2.
    const jitterMs = Math.floor(Math.random() * 3 * 60 * 1000);
    const seedAt = new Date(Date.now() + jitterMs).toISOString();

    await supabase
      .from('organizations')
      .update({
        next_scheduled_backup_at: seedAt,
      } as AppDatabase['public']['Tables']['organizations']['Update'] as never)
      .eq('id', organizationId)
      .is('next_scheduled_backup_at', null); // last-second guard against races
  }
}

/**
 * Toggle automation for a single tracked form.
 */
export async function toggleFormAutomation(
  trackedFormId: string,
  enabled: boolean
): Promise<{ success: boolean; error?: string }> {
  // Backups continue for every tracked form. Plan limits now affect UI access
  // and upgrade prompts, not whether automation can be enabled.
  const updateData: Record<string, unknown> = { automation_enabled: enabled };

  // If disabling, clear next_run_at so per-form UI shows as paused.
  // (Backup scheduling is now org-level; this column is for legacy display only.)
  if (!enabled) {
    updateData.next_run_at = null;
  }

  const { error } = await supabase
    .from('tracked_forms')
    .update(updateData as AppDatabase['public']['Tables']['tracked_forms']['Update'] as never)
    .eq('id', trackedFormId);

  if (error) {
    return { success: false, error: error.message };
  }

  // If enabling and the org has no scheduled backup time, seed it near-immediate
  // so the next planner tick picks the org up. Existing org schedules are not
  // moved earlier — toggling one form back on shouldn't reset everyone else's
  // daily timing.
  if (enabled) {
    const { data: form } = await supabase
      .from('tracked_forms')
      .select('organization_id')
      .eq('id', trackedFormId)
      .single();

    if (form) {
      const orgId = (form as { organization_id: string }).organization_id;
      const jitterMs = Math.floor(Math.random() * 3 * 60 * 1000);
      const seedAt = new Date(Date.now() + jitterMs).toISOString();

      await supabase
        .from('organizations')
        .update({
          next_scheduled_backup_at: seedAt,
        } as AppDatabase['public']['Tables']['organizations']['Update'] as never)
        .eq('id', orgId)
        .is('next_scheduled_backup_at', null);
    }
  }

  return { success: true };
}

/**
 * Enqueue a manual backup job for a tracked form.
 */
export async function enqueueManualBackup(
  trackedFormId: string
): Promise<{ success: boolean; jobId?: string; error?: string }> {
  // Get the tracked form to find org_id
  const { data: form, error: formError } = await supabase
    .from('tracked_forms')
    .select('id, organization_id, manual_backups_used')
    .eq('id', trackedFormId)
    .single();

  if (formError || !form) {
    return { success: false, error: 'Tracked form not found' };
  }

  // Check manual cap
  const { data: org } = await supabase
    .from('organizations')
    .select('plan_tier_id')
    .eq('id', (form as unknown as TrackedForm).organization_id)
    .single();

  if (org) {
    const { data: tier } = await supabase
      .from('plan_tiers')
      .select('manual_backup_cap')
      .eq('id', (org as unknown as Organization).plan_tier_id)
      .single();

    if (tier) {
      const cap = (tier as unknown as PlanTier).manual_backup_cap;
      if (cap >= 0 && (form as unknown as TrackedForm).manual_backups_used >= cap) {
        return { success: false, error: 'Manual backup cap reached. Upgrade your plan for more.' };
      }
    }
  }

  const typedForm = form as unknown as TrackedForm;

  // Create manual backup job
  const { data: job, error: jobError } = await supabase
    .from('backup_jobs')
    .insert({
      organization_id: typedForm.organization_id,
      tracked_form_id: trackedFormId,
      trigger_type: 'manual',
      state: 'pending',
      priority: 10, // Manual backups run before scheduled
      scheduled_at: new Date().toISOString(),
      idempotency_key: `manual:${trackedFormId}:${Date.now()}`,
    } as AppDatabase['public']['Tables']['backup_jobs']['Insert'] as never)
    .select('id')
    .single();

  if (jobError) {
    return { success: false, error: jobError.message };
  }

  // Increment manual_backups_used
  await supabase
    .from('tracked_forms')
    .update({
      manual_backups_used: typedForm.manual_backups_used + 1,
    } as AppDatabase['public']['Tables']['tracked_forms']['Update'] as never)
    .eq('id', trackedFormId);

  return { success: true, jobId: (job as unknown as { id: string }).id };
}

// ---------------------------------------------------------------------------
// Plan Usage
// ---------------------------------------------------------------------------

export async function getPlanUsage(): Promise<PlanUsage | null> {
  const org = await getUserOrganization();
  if (!org) return null;

  const tier = org.plan_tiers;
  if (!tier) return null;

  // Count tracked forms with automation enabled
  const { count: formsTracked } = await supabase
    .from('tracked_forms')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', org.id)
    .eq('automation_enabled', true);

  // Count failures in last 24h
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { count: failures24h } = await supabase
    .from('automation_events')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', org.id)
    .eq('level', 'error')
    .gte('created_at', yesterday);

  // Sum manual_backups_used across all forms
  const { data: manualData } = await supabase
    .from('tracked_forms')
    .select('manual_backups_used')
    .eq('organization_id', org.id);

  const totalManualUsed = (manualData || []).reduce(
    (sum, f) => sum + ((f as unknown as TrackedForm).manual_backups_used || 0),
    0
  );

  const manualCap = tier.manual_backup_cap;

  return {
    formsTracked: formsTracked || 0,
    formLimit: tier.form_limit,
    failures24h: failures24h || 0,
    manualRemaining: manualCap < 0 ? Infinity : Math.max(0, manualCap - totalManualUsed),
    manualCap: manualCap < 0 ? Infinity : manualCap,
    retentionDays: tier.retention_days,
    trialEndsAt: org.billing_status === 'trialing' ? org.trial_ends_at : null,
    tierName: tier.display_name,
  };
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export async function getAutomationEvents(limit = 50): Promise<AutomationEvent[]> {
  const { data, error } = await supabase
    .from('automation_events')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[automation] Error fetching events:', error.message);
    return [];
  }

  return (data || []) as unknown as AutomationEvent[];
}
